/**
 * Gathers lightweight context about the current document state
 * (active sheet, selected slide, cursor position) to send with each chat message.
 * This lets the LLM know where the user is without wasting a tool call.
 *
 * IMPORTANT: This must NEVER crash or hang. Every call is wrapped with a
 * timeout and multiple layers of try/catch so the chat flow is never blocked.
 */

export interface DocumentContext {
  /** Excel: active sheet name */
  activeSheet?: string;
  /** Excel: selected range address (e.g. "A1:C5") */
  selectedRange?: string;
  /** Excel: total number of worksheets */
  sheetCount?: number;
  /** Excel: row count of the active sheet's used range */
  usedRangeRows?: number;
  /** Excel: column count of the active sheet's used range */
  usedRangeCols?: number;
  /** PowerPoint: 0-based index of the selected slide */
  selectedSlideIndex?: number;
  /** PowerPoint: total number of slides */
  totalSlides?: number;
  /** Word: first 200 chars of selected text (empty if no selection) */
  selectedText?: string;
  /** Word: total paragraph count (rough page proxy) */
  totalParagraphs?: number;
}

/** Race a promise against a timeout. Returns null if timeout wins. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
  ]);
}

export async function getDocumentContext(): Promise<DocumentContext | null> {
  try {
    if (typeof Office === 'undefined' || !Office?.context?.host) return null;
    const host = Office.context.host;

    let getter: Promise<DocumentContext>;
    switch (host) {
      case Office.HostType.Excel:
        getter = getExcelContext();
        break;
      case Office.HostType.PowerPoint:
        getter = getPowerPointContext();
        break;
      case Office.HostType.Word:
        getter = getWordContext();
        break;
      default:
        return null;
    }

    // 3-second timeout: if Office.js hangs (document transition), just skip context
    return await withTimeout(getter, 3000);
  } catch {
    return null;
  }
}

async function getExcelContext(): Promise<DocumentContext> {
  return Excel.run(async (context) => {
    const result: DocumentContext = {};

    try {
      const sheets = context.workbook.worksheets;
      sheets.load('items/name');

      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.load('name');

      const selection = context.workbook.getSelectedRange();
      selection.load('address');

      const usedRange = sheet.getUsedRangeOrNullObject(true);
      usedRange.load('rowCount,columnCount');

      await context.sync();

      result.activeSheet = sheet.name;
      result.selectedRange = selection.address;
      result.sheetCount = sheets.items.length;
      result.usedRangeRows = usedRange.isNullObject ? 0 : usedRange.rowCount;
      result.usedRangeCols = usedRange.isNullObject ? 0 : usedRange.columnCount;
    } catch {
      // Partial failure is fine — return whatever we have
    }

    return result;
  });
}

async function getPowerPointContext(): Promise<DocumentContext> {
  return PowerPoint.run(async (context) => {
    const result: DocumentContext = {};

    try {
      const slides = context.presentation.slides;
      slides.load('items/id');
      await context.sync();

      result.totalSlides = slides.items.length;
      result.selectedSlideIndex = 0;

      try {
        const selectedSlides = context.presentation.getSelectedSlides();
        selectedSlides.load('items/id');
        await context.sync();

        if (selectedSlides.items.length > 0) {
          const selectedId = selectedSlides.items[0].id;
          const idx = slides.items.findIndex((s) => s.id === selectedId);
          if (idx >= 0) result.selectedSlideIndex = idx;
        }
      } catch {
        // getSelectedSlides may not be available in all API sets
      }
    } catch {
      // Partial failure is fine
    }

    return result;
  });
}

async function getWordContext(): Promise<DocumentContext> {
  return Word.run(async (context) => {
    const result: DocumentContext = {};

    try {
      const selection = context.document.getSelection();
      selection.load('text');

      const body = context.document.body;
      body.load('paragraphs/items');

      await context.sync();

      const text = selection.text || '';
      result.selectedText = text.length > 200 ? text.slice(0, 200) + '...' : text;
      result.totalParagraphs = body.paragraphs.items.length;
    } catch {
      // Partial failure is fine
    }

    return result;
  });
}
